import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Calendar, GitCompare, Search, Users } from 'lucide-react';
import TeamManagement from './TeamManagement';
import DateRangeBar from './DateRangeBar';
import { queryDateParams } from '../utils/dateRange';
import './TeamSearch.css';

function formatDate(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
}

function recordLabel(wins = 0, losses = 0, ties = 0) {
  return ties ? `${wins}–${losses}–${ties}` : `${wins}–${losses}`;
}

function TeamSearch({ token, nightMode, onTeamClick, onMatchClick, onBack }) {
  const [tab, setTab] = useState('find');
  const [query, setQuery] = useState('');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedName, setSelectedName] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [includeSimilar, setIncludeSimilar] = useState(true);
  const [range, setRange] = useState({ preset: 'all', dateFrom: '', dateTo: '' });

  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const [compareQueryA, setCompareQueryA] = useState('');
  const [compareQueryB, setCompareQueryB] = useState('');
  const [suggestionsA, setSuggestionsA] = useState([]);
  const [suggestionsB, setSuggestionsB] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareFuzzy, setCompareFuzzy] = useState(true);

  const fetchTeams = useCallback(async (search) => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/teams/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: search ? { q: search } : {}
      });
      setTeams(response.data.teams || []);
    } catch (err) {
      setError('Couldn’t search teams. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchTeams(query.trim());
    }, query.trim() ? 180 : 0);
    return () => clearTimeout(handle);
  }, [query, fetchTeams]);

  const fetchDetails = useCallback(async (name, fuzzy, nextRange) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/teams/${encodeURIComponent(name)}/details`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { fuzzy: fuzzy ? 'true' : 'false', ...queryDateParams(nextRange.dateFrom, nextRange.dateTo) }
        }
      );
      setTeamDetails(response.data);
    } catch (err) {
      setError('Couldn’t load games. ' + (err.response?.data?.message || err.message));
    } finally {
      setDetailLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedName) {
      fetchDetails(selectedName, includeSimilar, range);
    }
  }, [selectedName, includeSimilar, range, fetchDetails]);

  const searchCompareSuggestions = async (value, side) => {
    if (!value || value.length < 1) {
      if (side === 'A') setSuggestionsA([]);
      else setSuggestionsB([]);
      return;
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/teams/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { q: value }
      });
      const list = (response.data.teams || []).slice(0, 8);
      if (side === 'A') setSuggestionsA(list);
      else setSuggestionsB(list);
    } catch (err) {
      // Suggestions are optional.
    }
  };

  const fetchCompare = useCallback(async (nameA, nameB, fuzzy) => {
    if (!nameA || !nameB) {
      setCompareData(null);
      return;
    }
    try {
      setCompareLoading(true);
      setError('');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/teams/compare`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { a: nameA, b: nameB, fuzzy: fuzzy ? 'true' : 'false' }
      });
      setCompareData(response.data);
    } catch (err) {
      setError('Couldn’t compare teams. ' + (err.response?.data?.message || err.message));
    } finally {
      setCompareLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (tab === 'compare' && compareA && compareB) {
      fetchCompare(compareA, compareB, compareFuzzy);
    }
  }, [tab, compareA, compareB, compareFuzzy, fetchCompare]);

  const openTeam = (name) => {
    setSelectedName(name);
    setError('');
  };

  const startCompare = (name, otherName) => {
    setCompareA(name);
    setCompareQueryA(name);
    setCompareB(otherName || '');
    setCompareQueryB(otherName || '');
    setSuggestionsA([]);
    setSuggestionsB([]);
    setTab('compare');
  };

  const pickCompareTeam = (side, name) => {
    if (side === 'A') {
      setCompareA(name);
      setCompareQueryA(name);
      setSuggestionsA([]);
    } else {
      setCompareB(name);
      setCompareQueryB(name);
      setSuggestionsB([]);
    }
  };

  return (
    <div className={`team-search ${nightMode ? 'night-mode' : ''}`}>
      <div className="team-search-toolbar">
        <div className="team-search-toolbar-left">
          {selectedName && tab === 'find' ? (
            <button type="button" className="text-back" onClick={() => { setSelectedName(null); setTeamDetails(null); }}>
              <ArrowLeft size={16} /> Teams
            </button>
          ) : (
            <h1>Teams</h1>
          )}
        </div>
        {onBack ? (
          <button type="button" className="ghost-link" onClick={onBack}>Scoreboard</button>
        ) : null}
      </div>

      <nav className="team-search-tabs" aria-label="Team sections">
        <button type="button" className={tab === 'find' ? 'active' : ''} onClick={() => setTab('find')}>Find</button>
        <button type="button" className={tab === 'compare' ? 'active' : ''} onClick={() => setTab('compare')}>Compare</button>
        <button type="button" className={tab === 'rosters' ? 'active' : ''} onClick={() => setTab('rosters')}>Rosters</button>
      </nav>

      {error && <div className="team-search-error">{error}</div>}

      {tab === 'find' && !selectedName && (
        <div className="find-panel">
          <div className="team-search-bar">
            <Search size={18} />
            <input
              type="search"
              placeholder="Search teams…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <p className="hint">
            Type a name to find teams and close spellings, then open their games.
          </p>

          {loading ? (
            <div className="team-search-empty">Searching…</div>
          ) : teams.length === 0 ? (
            <div className="team-search-empty">
              <Users size={28} />
              <p>{query ? 'No teams match that search.' : 'No teams yet. Save a match to start.'}</p>
            </div>
          ) : (
            <div className="team-result-list">
              {teams.map((team) => (
                <div key={team.name} className="team-result">
                  <button type="button" className="team-result-main" onClick={() => openTeam(team.name)}>
                    <span className="team-result-name" title={team.name}>{team.name}</span>
                    <span className="team-result-record">{recordLabel(team.wins, team.losses, team.ties)}</span>
                    <span className="team-result-meta">{team.matches} games</span>
                    {team.similar?.length > 0 && (
                      <span className="similar-note">
                        Also: {team.similar.slice(0, 3).join(', ')}
                        {team.similar.length > 3 ? ` +${team.similar.length - 3}` : ''}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="compare-chip"
                    onClick={() => startCompare(team.name, team.similar?.[0])}
                  >
                    <GitCompare size={14} /> Compare
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'find' && selectedName && (
        <TeamGames
          name={selectedName}
          details={teamDetails}
          loading={detailLoading}
          includeSimilar={includeSimilar}
          onToggleSimilar={setIncludeSimilar}
          onMatchClick={onMatchClick}
          onTeamClick={onTeamClick}
          onCompare={(other) => startCompare(selectedName, other)}
          range={range}
          onRangeChange={setRange}
        />
      )}

      {tab === 'rosters' && (
        <TeamManagement
          token={token}
          nightMode={nightMode}
          onBack={() => setTab('find')}
          embedded
        />
      )}

      {tab === 'compare' && (
        <ComparePanel
          compareA={compareA}
          compareB={compareB}
          compareQueryA={compareQueryA}
          compareQueryB={compareQueryB}
          suggestionsA={suggestionsA}
          suggestionsB={suggestionsB}
          compareData={compareData}
          compareLoading={compareLoading}
          compareFuzzy={compareFuzzy}
          onQueryA={(value) => {
            setCompareQueryA(value);
            searchCompareSuggestions(value, 'A');
          }}
          onQueryB={(value) => {
            setCompareQueryB(value);
            searchCompareSuggestions(value, 'B');
          }}
          onPick={pickCompareTeam}
          onFuzzy={setCompareFuzzy}
          onMatchClick={onMatchClick}
        />
      )}
    </div>
  );
}

function TeamGames({ name, details, loading, includeSimilar, onToggleSimilar, onMatchClick, onTeamClick, onCompare, range, onRangeChange }) {
  if (loading && !details) {
    return <div className="team-search-empty">Loading games…</div>;
  }

  const aliases = (details?.aliases || []).filter((alias) => alias !== name);
  const stats = details?.stats || {};

  return (
    <div className="team-games">
      <DateRangeBar
        preset={range.preset}
        dateFrom={range.dateFrom}
        dateTo={range.dateTo}
        onChange={onRangeChange}
      />
      <div className="team-games-header">
        <div>
          <h2>{details?.teamName || name}</h2>
          <p>{stats.totalMatches || 0} games · {recordLabel(stats.wins, stats.losses, stats.ties)} · {stats.winPercentage || 0}% win</p>
        </div>
        <div className="team-games-actions">
          {onTeamClick && (
            <button type="button" className="ghost-link" onClick={() => onTeamClick(name, range)}>Full stats</button>
          )}
          <button type="button" className="compare-chip" onClick={() => onCompare(aliases[0])}>
            <GitCompare size={14} /> Compare
          </button>
        </div>
      </div>

      <label className="similar-toggle">
        <input
          type="checkbox"
          checked={includeSimilar}
          onChange={(e) => onToggleSimilar(e.target.checked)}
        />
        Include similar names
      </label>

      {aliases.length > 0 && includeSimilar && (
        <div className="alias-row">
          <span>Also counting</span>
          {aliases.map((alias) => (
            <button key={alias} type="button" className="alias-chip" onClick={() => onCompare(alias)}>
              {alias}
            </button>
          ))}
        </div>
      )}

      {details.matches?.length === 0 ? (
        <div className="team-search-empty">No games for this team yet.</div>
      ) : (
        <div className="game-list">
          {details.matches.map((match) => {
            const aliasesForTeam = details.aliases || [name];
            const isTeamA = aliasesForTeam.some((alias) => alias.toLowerCase() === match.teamA.toLowerCase());
            const opponent = isTeamA ? match.teamB : match.teamA;
            const ourScore = isTeamA ? match.scoreA : match.scoreB;
            const theirScore = isTeamA ? match.scoreB : match.scoreA;
            const result = ourScore > theirScore ? 'win' : theirScore > ourScore ? 'loss' : 'tie';
            const recordedAs = isTeamA ? match.teamA : match.teamB;

            return (
              <button
                key={match._id}
                type="button"
                className={`game-row ${result}`}
                onClick={() => onMatchClick && onMatchClick(match)}
              >
                <span className="game-date">
                  <Calendar size={14} />
                  {formatDate(match.date)}
                </span>
                <span className="game-matchup">
                  vs <strong>{opponent}</strong>
                  {recordedAs !== name && <em> as {recordedAs}</em>}
                </span>
                <span className={`game-score ${result}`}>{ourScore}–{theirScore}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ComparePanel({
  compareA,
  compareB,
  compareQueryA,
  compareQueryB,
  suggestionsA,
  suggestionsB,
  compareData,
  compareLoading,
  compareFuzzy,
  onQueryA,
  onQueryB,
  onPick,
  onFuzzy,
  onMatchClick
}) {
  return (
    <div className="compare-panel">
      <div className="compare-pickers">
        <TeamPicker
          label="Team A"
          value={compareQueryA}
          selected={compareA}
          suggestions={suggestionsA}
          onChange={onQueryA}
          onPick={(name) => onPick('A', name)}
        />
        <TeamPicker
          label="Team B"
          value={compareQueryB}
          selected={compareB}
          suggestions={suggestionsB}
          onChange={onQueryB}
          onPick={(name) => onPick('B', name)}
        />
      </div>

      <label className="similar-toggle">
        <input
          type="checkbox"
          checked={compareFuzzy}
          onChange={(e) => onFuzzy(e.target.checked)}
        />
        Include similar spellings
      </label>

      {!compareA || !compareB ? (
        <div className="team-search-empty">
          <GitCompare size={28} />
          <p>Pick two teams to compare records and head-to-head games. Similar names like “Eagles” and “Eagles VB” are grouped when the toggle is on.</p>
        </div>
      ) : compareLoading || !compareData ? (
        <div className="team-search-empty">Comparing…</div>
      ) : (
        <CompareResults data={compareData} onMatchClick={onMatchClick} />
      )}
    </div>
  );
}

function TeamPicker({ label, value, selected, suggestions, onChange, onPick }) {
  return (
    <div className="team-picker">
      <label>{label}</label>
      <div className="team-search-bar compact">
        <Search size={16} />
        <input
          type="search"
          placeholder="Search team…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && value.trim()) {
              onPick(value.trim());
            }
          }}
        />
      </div>
      {selected && selected !== value && (
        <div className="selected-name">Using {selected}</div>
      )}
      {suggestions.length > 0 && (
        <div className="picker-suggestions">
          {suggestions.map((team) => (
            <button key={team.name} type="button" onClick={() => onPick(team.name)}>
              <span>{team.name}</span>
              <small>{team.matches} games</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CompareResults({ data, onMatchClick }) {
  const a = data.teamA;
  const b = data.teamB;
  const h2h = data.headToHead;
  const extraA = (a.aliases || []).filter((name) => name !== a.name);
  const extraB = (b.aliases || []).filter((name) => name !== b.name);

  return (
    <div className="compare-results">
      <div className="compare-cards">
        <CompareCard team={a} extras={extraA} />
        <CompareCard team={b} extras={extraB} />
      </div>

      <section className="h2h">
        <h3>Head-to-head</h3>
        <p className="h2h-score">
          {a.name} {h2h.winsA}–{h2h.winsB}{h2h.ties ? `–${h2h.ties}` : ''} {b.name}
        </p>
        {h2h.matches.length === 0 ? (
          <p className="hint">No games between these teams yet.</p>
        ) : (
          <div className="game-list">
            {h2h.matches.map((match) => (
              <button
                key={match._id}
                type="button"
                className={`game-row ${match.resultForA}`}
                onClick={() => onMatchClick && onMatchClick(match)}
              >
                <span className="game-date">
                  <Calendar size={14} />
                  {formatDate(match.date)}
                </span>
                <span className="game-matchup">
                  {match.teamA} vs {match.teamB}
                </span>
                <span className={`game-score ${match.resultForA}`}>
                  {match.scoreForA}–{match.scoreForB}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CompareCard({ team, extras }) {
  const s = team.stats || {};
  return (
    <article className="compare-card">
      <h3>{team.name}</h3>
      <p className="compare-record">{recordLabel(s.wins, s.losses, s.ties)}</p>
      <p className="compare-meta">{s.totalMatches || 0} games · {s.winPercentage || 0}% win</p>
      <p className="compare-meta">Pts {s.avgPointsFor}–{s.avgPointsAgainst} avg</p>
      {extras.length > 0 && (
        <p className="similar-note">Also: {extras.join(', ')}</p>
      )}
    </article>
  );
}

export default TeamSearch;
